import { useCallback } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useManagePolicyQuery, useSetManagePolicyMutation } from '../store/api';

import { ManagePolicyView } from './ManagePolicyView';

/** Container: resolves the org-wide manage policy (minimum-notice window +
 * cancel flag), falling back to the server default when unconfigured, and
 * upserts on each change. The self-reschedule flag isn't surfaced yet, so its
 * current value is preserved on every write. Org-wide only in this UI —
 * per-encounter-type and per-VIP overrides live in the API. */
export function ManagePolicySection() {
  const { data, isLoading } = useManagePolicyQuery();
  const [setPolicy, { isLoading: isSaving }] = useSetManagePolicyMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { plural } = useSubjectLanguageVariants();
  const { schedulingSettings, error: errorTracking } = useEventTracking();

  const orgWide = data?.items.find(item => !item.encounter_type_id);
  const minimumNoticeHours =
    orgWide?.minimum_notice_hours ?? data?.default_notice_hours ?? 24;
  const cancelEnabled = orgWide?.cancel_enabled ?? true;
  const rescheduleEnabled = orgWide?.reschedule_enabled ?? true;

  const savePolicy = useCallback(
    (next: {
      minimum_notice_hours: number;
      reschedule_enabled: boolean;
      cancel_enabled: boolean;
    }) =>
      (async () => {
        try {
          await setPolicy({ setManageRequest: next }).unwrap();
        } catch {
          enqueueSnackbar('Could not update the scheduling policy', {
            variant: 'error',
          });
          errorTracking.track(
            'Could not update the scheduling manage policy',
            'scheduling_manage_policy_update',
          );
        }
      })(),
    [setPolicy, enqueueSnackbar, errorTracking],
  );

  const handleNoticeChange = useCallback(
    (hours: number) => {
      schedulingSettings.manageNoticeChanged({ minimum_notice_hours: hours });
      void savePolicy({
        minimum_notice_hours: hours,
        reschedule_enabled: rescheduleEnabled,
        cancel_enabled: cancelEnabled,
      });
    },
    [savePolicy, schedulingSettings, rescheduleEnabled, cancelEnabled],
  );

  const handleCancelEnabledChange = useCallback(
    (enabled: boolean) => {
      schedulingSettings.selfCancelToggled({ enabled });
      void savePolicy({
        minimum_notice_hours: minimumNoticeHours,
        reschedule_enabled: rescheduleEnabled,
        cancel_enabled: enabled,
      });
    },
    [savePolicy, schedulingSettings, minimumNoticeHours, rescheduleEnabled],
  );

  return (
    <ManagePolicyView
      minimumNoticeHours={minimumNoticeHours}
      cancelEnabled={cancelEnabled}
      subjectPlural={plural}
      isLoading={isLoading}
      disabled={isSaving}
      onNoticeChange={handleNoticeChange}
      onCancelEnabledChange={handleCancelEnabledChange}
    />
  );
}
